import { supabase } from "@/integrations/supabase/client";
import { withOwner } from "@/lib/supabase/withOwner";
import type { KBTemplate } from "@/lib/pharmacologyKnowledgeBase";
import { bestAlias, describeMatch, type AliasMatch } from "@/lib/pharmacology/aliasResolver";
import { toFormulationInserts, toSubstanceInsert } from "@/lib/pharmacology/templateImport";
import {
  canonicalKeyForSubstance,
  canonicalText,
  findExistingCanonical,
  substanceTokens,
  templateTokens,
  uniqueStrings,
  type SubstanceRow,
} from "@/lib/pharmacology/substanceCatalog";

export type SmartSubstanceResolution = {
  input: string;
  match: AliasMatch | null;
  template: KBTemplate | null;
  canonicalSubstance: any | null;
  duplicateSubstances: any[];
  message: string;
};

export function aliasesForTemplate(template: KBTemplate): string[] {
  return templateTokens(template);
}

export function isSameCanonicalSubstance(substance: any, template: KBTemplate) {
  const target = canonicalText(template.normalized_key ?? template.name);
  return canonicalKeyForSubstance(substance, [template]) === target;
}

export function findCanonicalSubstance(substances: any[], template: KBTemplate, userId?: string | null) {
  return findExistingCanonical(substances as SubstanceRow[], template, userId)?.primary ?? null;
}

export function findDuplicateAliasSubstances(substances: any[], template: KBTemplate, userId?: string | null) {
  const group = findExistingCanonical(substances as SubstanceRow[], template, userId);
  return group?.duplicates ?? [];
}

export function resolveSmartSubstance(input: string, substances: any[]): SmartSubstanceResolution {
  const match = bestAlias(input, 0.78);
  const template = match?.template ?? null;
  if (!template) {
    return {
      input,
      match: null,
      template: null,
      canonicalSubstance: null,
      duplicateSubstances: [],
      message: "Sem correspondência segura na base de conhecimento. Você pode criar manualmente, mas precisa revisar PK/PD e aliases.",
    };
  }

  const group = findExistingCanonical(substances as SubstanceRow[], template);
  const canonical = group?.primary ?? null;
  const duplicates = group?.duplicates ?? [];
  const base = describeMatch(match);
  const existingMsg = canonical ? ` Canônico já existente: ${canonical.name}.` : " O canônico ainda não foi criado no seu laboratório.";
  const duplicateMsg = duplicates.length
    ? ` ${duplicates.length} registro(s) redundante(s) foram agrupados visualmente e não serão recriados.`
    : "";
  return { input, match, template, canonicalSubstance: canonical, duplicateSubstances: duplicates, message: `${base}${existingMsg}${duplicateMsg}` };
}

async function readVisibleSubstances() {
  const { data, error } = await (supabase as any).from("substances").select("*");
  if (error) throw error;
  return (data ?? []) as SubstanceRow[];
}

async function ensureTemplateFormulations(template: KBTemplate, substance: any, user: any) {
  const desired = toFormulationInserts(template);
  if (!desired.length) return 0;

  const { data: current = [], error: readError } = await (supabase as any)
    .from("substance_formulations")
    .select("*")
    .eq("substance_id", substance.id);
  if (readError) throw readError;

  const existingNames = new Set((current ?? []).map((row: any) => canonicalText(row.formulation_name)));
  const missing = desired
    .filter((row: any) => !existingNames.has(canonicalText(row.formulation_name)))
    .map((row) => withOwner({ ...row, substance_id: substance.id }, user));

  if (!missing.length) return 0;
  const { error } = await (supabase as any).from("substance_formulations").insert(missing);
  if (error) {
    // Um canônico global pode ser somente leitura. Nesse caso, a substância continua utilizável.
    if (substance.is_global || substance.owner_id !== user?.id) return 0;
    throw error;
  }
  return missing.length;
}

export async function ensureCanonicalSubstanceFromTemplate(template: KBTemplate, user: any, existingSubstances: any[] = []) {
  if (!user?.id) throw new Error("Sessão expirada. Faça login novamente.");

  const visible = existingSubstances.length ? existingSubstances as SubstanceRow[] : await readVisibleSubstances();
  const existingGroup = findExistingCanonical(visible, template, user.id);
  if (existingGroup?.primary) {
    const formulationsCreated = await ensureTemplateFormulations(template, existingGroup.primary, user);
    return {
      substance: existingGroup.primary,
      created: false,
      formulationsCreated,
      duplicateSubstances: existingGroup.duplicates,
    };
  }

  const payload = withOwner(toSubstanceInsert(template), user);
  const { data: substance, error } = await (supabase as any).from("substances").insert(payload).select().single();
  if (error) {
    // Proteção contra corrida ou base desatualizada: refaz leitura antes de desistir.
    const fresh = await readVisibleSubstances();
    const recovered = findExistingCanonical(fresh, template, user.id);
    if (recovered?.primary) {
      const formulationsCreated = await ensureTemplateFormulations(template, recovered.primary, user);
      return { substance: recovered.primary, created: false, formulationsCreated, duplicateSubstances: recovered.duplicates };
    }
    throw error;
  }

  const formulationsCreated = await ensureTemplateFormulations(template, substance, user);
  return { substance, created: true, formulationsCreated, duplicateSubstances: [] };
}

export async function ensureCanonicalSubstanceFromInput(input: string, user: any, existingSubstances: any[] = []) {
  const resolution = resolveSmartSubstance(input, existingSubstances);
  if (!resolution.template) return { ...resolution, substance: null, created: false, formulationsCreated: 0 };
  const ensured = await ensureCanonicalSubstanceFromTemplate(resolution.template, user, existingSubstances);
  return { ...resolution, ...ensured };
}

export function displayMedicationName(medication: any) {
  const brand = medication?.brand_name || medication?.free_text_name;
  const canonical = medication?.substances?.name;
  if (brand && canonical && canonicalText(brand) !== canonicalText(canonical)) return `${brand} → ${canonical}`;
  return canonical || brand || "Medicação sem nome";
}

export function templateSummary(template: KBTemplate) {
  const aliases = uniqueStrings(templateTokens(template)).slice(0, 8).join(", ");
  const curve = template.default_curve_model ?? template.formulations?.[0]?.curve_model ?? "curva não estruturada";
  return { aliases, curve };
}

export function mergeCanonicalAliases(existing: any, template: KBTemplate) {
  const aliases = uniqueStrings([...substanceTokens(existing), ...templateTokens(template)]);
  return {
    brand_names: uniqueStrings([existing?.brand_names, template.brand_names, template.brands]),
    brands: uniqueStrings([existing?.brands, template.brands, template.brand_names]),
    synonyms: uniqueStrings([existing?.synonyms, aliases]),
    reference_names: uniqueStrings([existing?.reference_names, template.reference_names]),
    common_misspellings: uniqueStrings([existing?.common_misspellings, template.common_misspellings]),
  };
}
